/**
 * API Service optimized for react-oidc-context
 */
import { User } from 'oidc-client-ts';
import { API_BASE_URL } from '../layout/MainLayout';

/**
 * Standard API request function
 * Enhanced to ensure Content-Type headers are consistently applied
 */
export const apiRequest = async <T = any>(
  endpoint: string,
  method = 'GET',
  data?: any,
  user?: User | null
): Promise<T> => {
  if (!user || !user.access_token) {
    throw new Error('Usuário não autenticado ou token não disponível');
  }

  // IMPORTANTE: Configure headers with explicit Content-Type for ALL request types
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${user.access_token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };

  // Request configuration
  const config: RequestInit = {
    method,
    headers,
    credentials: 'omit' // Important for AWS API Gateway
  };

  // Detailed logging for debugging
  console.log(`API Request to ${endpoint} [${method}]:`, {
    url: `${API_BASE_URL}${endpoint}`,
    method: method,
    headers: JSON.stringify(headers),
  });

  // Add body data for ALL request types that support body
  if (method === 'POST' || method === 'PUT' || method === 'PATCH') {
    // Always send a serialized JSON object, even if empty
    const bodyContent = data || {};
    config.body = JSON.stringify(bodyContent);

    // Detailed logging of request body
    console.log(`Request body for ${endpoint}:`, config.body);
  }

  // Make the request with explicit URL
  const url = `${API_BASE_URL}${endpoint}`;
  console.log(`Making fetch request to: ${url}`);

  const response = await fetch(url, config);

  // Log response status and headers for debugging
  console.log(`Response from ${endpoint}:`, {
    status: response.status,
    statusText: response.statusText,
    // Use Array.from instead of spread operator to avoid TypeScript downlevel iteration error
    headers: Object.fromEntries(Array.from(response.headers.entries()))
  });

  // Enhanced error handling
  if (!response.ok) {
    const errorText = await response.text();
    console.error(`Error response from ${endpoint}:`, {
      status: response.status,
      statusText: response.statusText,
      body: errorText
    });

    let errorMessage: string;

    try {
      const errorData = JSON.parse(errorText);
      errorMessage = errorData.message || errorData.error || `${response.status}: ${response.statusText}`;
    } catch (e) {
      errorMessage = errorText || `${response.status}: ${response.statusText}`;
    }

    throw new Error(errorMessage);
  }

  // Handle empty responses correctly
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return {} as T;
  }

  // Parse response safely
  try {
    const data = await response.json();
    return data;
  } catch (error) {
    console.warn(`Failed to parse JSON response from ${endpoint}:`, error);
    return {} as T;
  }
};

/**
 * API client factory - creates an API client using the given user
 */
export const createApiClient = (user: User | null) => ({
  get: <T = any>(endpoint: string) => 
    apiRequest<T>(endpoint, 'GET', undefined, user),
    
  post: <T = any>(endpoint: string, data: any) => 
    apiRequest<T>(endpoint, 'POST', data, user),
    
  put: <T = any>(endpoint: string, data: any) => 
    apiRequest<T>(endpoint, 'PUT', data, user),
    
  patch: <T = any>(endpoint: string, data: any) => 
    apiRequest<T>(endpoint, 'PATCH', data, user),
    
  delete: <T = any>(endpoint: string) => 
    apiRequest<T>(endpoint, 'DELETE', undefined, user)
});

/**
 * Custom hook for using the API with current authenticated user
 */
export const useApi = (user: User | null) => {
  return createApiClient(user);
};